package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Reusable peer-marketplace listing card from Stitch `66b7e10b` / `3df3f7e4`.
 *
 * Copy and formatting remain caller-owned so every label can come from localized resources. The
 * image keeps the tokenized marketplace fallback visible while a remote photo loads or fails.
 */
@Composable
fun MarketplaceItemCard(
    title: String,
    sellerName: String,
    price: String,
    condition: String,
    imageContentDescription: String,
    modifier: Modifier = Modifier,
    imageUrl: String? = null,
    badgeLabel: String? = null,
    // Localized "City · 2h ago" line — the two things buyers scan first (FB Marketplace/Kijiji).
    metaLabel: String? = null,
    onClick: (() -> Unit)? = null,
    favorite: Boolean = false,
    favoriteContentDescription: String? = null,
    onToggleFavorite: (() -> Unit)? = null,
) {
    AppCard(modifier = modifier, onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(4f / 3f),
                contentAlignment = Alignment.TopStart,
            ) {
                BusinessCover(
                    imageUrl = imageUrl,
                    contentDescription = imageContentDescription,
                    iconName = "shopping_bag",
                )
                if (badgeLabel != null) {
                    StatusBadge(
                        label = badgeLabel,
                        modifier = Modifier.padding(AppTheme.spacing.sm),
                    )
                }
                if (onToggleFavorite != null) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(AppTheme.spacing.sm)
                            .size(AppTheme.sizes.iconCircle)
                            .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                            .clickable(onClick = onToggleFavorite),
                        contentAlignment = Alignment.Center,
                    ) {
                        AppIcon(
                            name = if (favorite) "favorite" else "favorite_border",
                            size = AppTheme.sizes.iconMd,
                            // Solid glyph when saved — tint alone was too subtle to read as a state.
                            filled = favorite,
                            tint = if (favorite) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                            contentDescription = favoriteContentDescription,
                        )
                    }
                }
            }
            Column(
                modifier = Modifier.padding(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
            ) {
                Text(
                    text = sellerName,
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.primary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = title,
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        text = price,
                        style = AppTheme.typography.labelMd,
                        color = AppTheme.colors.onSurface,
                    )
                    Text(
                        text = condition,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                        maxLines = 1,
                    )
                }
                if (metaLabel != null) {
                    Text(
                        text = metaLabel,
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

@Preview
@Composable
private fun MarketplaceItemCardPreview() {
    AppTheme {
        MarketplaceItemCard(
            title = "Handmade Persian Kilim",
            sellerName = "Amir",
            price = "$420.00",
            condition = "New",
            imageContentDescription = "Marketplace item",
            badgeLabel = "Featured",
        )
    }
}
